import { useRecordingForDoc } from '@/lib/recordings/use-recording-for-doc';
import { useRecordingPaused } from '@/lib/recordings/use-recording-paused';
import { IN_FLIGHT_STATUSES } from '@/lib/recordings/types/types';

/**
 * The lifecycle phase that matters to UI controls:
 *  - 'capturing'  — audio is being captured right now (status 'recording'). Live
 *                   controls (pause/stop) apply; the elapsed timer ticks.
 *  - 'processing' — capture has ended; the recording is uploading or
 *                   transcribing. No live controls — show "Saving…"; this
 *                   phase persists (driven by real status) until complete/failed.
 * A terminal recording (complete/failed) or no recording → useLiveRecording is null.
 */
export type LiveRecordingPhase = 'capturing' | 'processing';

export interface LiveRecording {
  recordingId: string;
  /** The manifest surface this recording was started from (design §3.6), or
   *  null for recordings that predate the surface-binding manifest field.
   *  Lets a caller (e.g. `SurfaceMount`'s bind effect) identify the surface
   *  the live recording actually started from, rather than guessing. */
  surfaceId: string | null;
  phase: LiveRecordingPhase;
  /** True while paused — synced across windows via the recording://paused broadcast. */
  isPaused: boolean;
}

/**
 * The single source of truth for a recording's live phase + paused state for an
 * entity — composed from the synced RecordingMeta (existence + status) and the
 * cross-window recording://paused broadcast. ANY surface reads this: the HUD +
 * the audio surface's record controls (only in the 'capturing' phase);
 * documents/library views can show a view-only indicator. Returns null when
 * there's no recording, or it has reached a terminal status.
 *
 * Why a phase (not a single isInProgress): post-capture statuses
 * (uploading/transcribing/…) are still "in flight" but are NOT capturing — live
 * pause/stop controls don't apply there. Collapsing them caused the transport
 * to re-show live controls during processing (the "Saving… flicker").
 */
export function useLiveRecording(entityId: string | null | undefined): LiveRecording | null {
  const recording = useRecordingForDoc(entityId);
  // Hook order must be stable, so always subscribe (empty id is a harmless no-op
  // filter — no event ever matches).
  const isPaused = useRecordingPaused(recording?.id ?? '');

  if (!recording || !IN_FLIGHT_STATUSES.includes(recording.status)) return null;

  return {
    recordingId: recording.id,
    surfaceId: recording.surfaceId,
    phase: recording.status === 'recording' ? 'capturing' : 'processing',
    isPaused,
  };
}
